import React, { useState, useEffect, useRef } from 'react';
import { X, Clock } from 'lucide-react';
import { AdvertisingModalConfig } from '../types';

interface AdvertisingModalProps {
  isOpen: boolean;
  config: AdvertisingModalConfig;
  onClose: () => void;
}

const AdvertisingModal: React.FC<AdvertisingModalProps> = ({ isOpen, config, onClose }) => {
  const [countdown, setCountdown] = useState<number>(0);
  const [canClose, setCanClose] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const clearCountdownInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    if (isOpen) {
      const timeout = config.timeoutSeconds || 5;
      setCountdown(timeout);
      setCanClose(timeout === 0);

      clearCountdownInterval();

      if (timeout > 0) {
        intervalRef.current = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              setCanClose(true);
              clearCountdownInterval();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } else {
      clearCountdownInterval();
      setCountdown(0);
      setCanClose(false);
    }

    return clearCountdownInterval;
  }, [isOpen, config.timeoutSeconds]);

  const handleClose = () => {
    if (canClose) {
      onClose();
    }
  };

  if (!isOpen || !config.enabled) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-slate-900/80 backdrop-blur-md z-50 transition-opacity duration-300 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        {/* Modal Content */}
        <div 
          className="relative bg-white border-2 border-sky-200 rounded-3xl max-w-2xl w-full shadow-2xl transform transition-all duration-300 ease-out overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Decorative gradient lines */}
          <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-sky-400 via-emerald-400 to-sky-400" />
          
          {/* Close Button */}
          <button 
            onClick={handleClose}
            disabled={!canClose}
            className={`absolute top-4 right-4 p-2 rounded-lg transition-colors z-10 ${
              canClose 
                ? 'text-slate-400 hover:text-slate-700 hover:bg-slate-100' 
                : 'text-slate-300 cursor-not-allowed'
            }`}
            title={canClose ? 'Cerrar' : `Espera ${countdown}s para cerrar`}
          >
            {!canClose ? (
              <div className="flex items-center gap-2 text-sky-500">
                <Clock className="w-5 h-5" />
                <span className="text-sm font-bold">{countdown}s</span>
              </div>
            ) : (
              <X className="w-5 h-5" />
            )}
          </button>

          {/* Content */}
          <div className="p-8 md:p-12">
            {/* Decorative accent */}
            <div className="absolute -top-20 -left-20 w-40 h-40 bg-sky-100 rounded-full blur-3xl pointer-events-none opacity-50" />
            <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-emerald-100 rounded-full blur-3xl pointer-events-none opacity-50" />
            
            {/* Title */}
            <h2 className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-sky-500 via-emerald-500 to-sky-500 mb-6 text-center leading-tight">
              {config.title}
            </h2>

            {/* Message */}
            <div className="text-slate-600 text-lg md:text-xl leading-relaxed mb-8 text-center max-w-lg mx-auto">
              {config.message}
            </div>

            {/* Link (if provided) */}
            {config.linkText && config.linkUrl && (
              <div className="mb-8 text-center">
                <a
                  href={config.linkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sky-500 hover:text-sky-600 font-semibold text-lg transition-colors underline decoration-sky-300 underline-offset-4 hover:decoration-sky-500"
                >
                  {config.linkText}
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            )}

            {/* CTA Button */}
            <div className="text-center">
              <button
                onClick={handleClose}
                disabled={!canClose}
                className={`font-bold py-4 px-10 rounded-xl transition-all duration-200 text-lg ${
                  canClose
                    ? 'bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-400 hover:to-emerald-400 text-white shadow-lg shadow-sky-200 hover:shadow-xl active:scale-95'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
              >
                {canClose ? config.buttonText : `Espera ${countdown}s...`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdvertisingModal;
